import { Component, computed, DOCUMENT, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { SidebarModule } from 'primeng/sidebar';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { AvailableUserScopes, featureScopes, userHasScope } from '../../../../../../../common/common-types/user-scopes';
import { setColorScheme } from '../../fb.theme';

interface NavItem {
    key: string;
    /** Translation key. */
    label: string;
    icon: string;
    route?: unknown[];
    scope?: AvailableUserScopes;
    items?: NavItem[];
    exact?: boolean;
}

type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'fb-org-theme';

@Component({
    selector: 'app-shell',
    templateUrl: './main.component.html',
    imports: [SidebarModule, RouterOutlet, RouterLink, RouterLinkActive, TranslatePipe, MenuModule, CommonModule]
})
export class MainComponent {
    private router = inject(Router);
    private document = inject(DOCUMENT);
    authService = inject(AuthService);

    private currentUrl = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map((e) => e.urlAfterRedirects)
        ),
        { initialValue: this.router.url }
    );

    isDispatch = computed(() => /^\/organizations\/[^/]+\/crm\/campaigns\/[^/]+/.test(this.currentUrl()));
    currentUser = this.authService.currentUser;
    currentOrgId = this.authService.organizationId;
    availableOrgs = computed(() => this.authService.availableOrganizations() ?? []);
    currentOrganization = computed(() => this.currentUser()?.organizationData?.[this.currentOrgId()]?.organizationName ?? '');

    // Desktop: false = icon rail, true = pinned open. Mobile: false = drawer closed.
    navOpen = signal(false);
    isMobile = signal(false);
    mode = signal<ThemeMode>('light');

    /** Rebuilt whenever the org changes so every link points at the current org. */
    navItems = computed<NavItem[]>(() => {
        const base = ['/organizations', this.currentOrgId()];
        return [
            { key: 'home', label: 'HOME', icon: 'ph-house', route: base, exact: true },
            { key: 'venues', label: 'VENUES', icon: 'ph-storefront', route: [...base, 'venues'] },
            {
                key: 'crm',
                label: 'CRM',
                icon: 'ph-users-three',
                items: [
                    {
                        key: 'customers',
                        label: 'CUSTOMERS',
                        icon: 'ph-address-book',
                        route: [...base, 'crm', 'customers'],
                        scope: featureScopes.customers
                    },
                    {
                        key: 'campaigns',
                        label: 'CAMPAIGNS',
                        icon: 'ph-megaphone',
                        route: [...base, 'crm', 'campaigns'],
                        scope: featureScopes.campaigns
                    },
                    {
                        key: 'triggers',
                        label: 'TRIGGERS',
                        icon: 'ph-lightning',
                        route: [...base, 'crm', 'triggers'],
                        scope: featureScopes.triggers
                    }
                ]
            },
            { key: 'funpay', label: 'FUNPAY', icon: 'ph-credit-card', route: [...base, 'funpay'], scope: featureScopes.funpay },
            { key: 'users', label: 'USERS', icon: 'ph-user-gear', route: [...base, 'admin', 'users'], scope: featureScopes.users }
        ];
    });

    /** Leaves the user can't open are dropped; groups with no visible leaves disappear. */
    visibleItems = computed<NavItem[]>(() =>
        this.navItems()
            .map((item) => (item.items ? { ...item, items: item.items.filter((c) => this.hasScope(c.scope)) } : item))
            .filter((item) => (item.items ? item.items.length > 0 : this.hasScope(item.scope)))
    );

    /** Header shortcut: every venue's calendar in the client app. */
    calendarMenu = computed<MenuItem[]>(() =>
        [...this.authService.accessibleVenues()]
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))
            .map((venue) => ({
                label: venue.name,
                icon: 'ph ph-calendar-blank',
                url: this.authService.clientAppUrl(venue._id, 'calendar'),
                target: '_blank'
            }))
    );

    themeLabel = computed(() => (this.mode() === 'dark' ? 'LIGHT_MODE' : 'DARK_MODE'));

    constructor() {
        this.initTheme();
        if (typeof window === 'undefined') {
            return;
        }
        const mql = window.matchMedia('(max-width: 1023px)');
        this.isMobile.set(mql.matches);
        mql.addEventListener('change', (e) => this.isMobile.set(e.matches));
    }

    hasScope(scope?: AvailableUserScopes): boolean {
        return userHasScope(this.currentUser(), this.currentOrgId(), scope);
    }

    async selectOrganization(orgId: string): Promise<void> {
        if (orgId === this.currentOrgId()) {
            return;
        }
        await this.authService.setOrganization(orgId);
        await this.router.navigateByUrl(`/organizations/${orgId}`, { replaceUrl: true });
        this.onNavigate();
    }

    /** Opens a collapsible group whose child is the current route. */
    hasActiveChild(item: NavItem): boolean {
        return (item.items ?? []).some(
            (c) =>
                !!c.route &&
                this.router.isActive(this.router.createUrlTree(c.route), {
                    paths: 'subset',
                    queryParams: 'ignored',
                    fragment: 'ignored',
                    matrixParams: 'ignored'
                })
        );
    }

    /** Clicking a group on the collapsed rail opens the sidebar so its children are reachable. */
    openForSubmenu(): void {
        if (!this.navOpen()) {
            this.navOpen.set(true);
        }
    }

    onNavigate(): void {
        if (this.isMobile()) {
            this.navOpen.set(false);
        }
    }

    toggleTheme(): void {
        this.applyTheme(this.mode() === 'dark' ? 'light' : 'dark');
    }

    private initTheme(): void {
        let stored: string | null = null;
        try {
            stored = localStorage.getItem(THEME_STORAGE_KEY);
        } catch {
            stored = null;
        }
        const prefersDark = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
        this.applyTheme(stored === 'dark' || stored === 'light' ? stored : prefersDark ? 'dark' : 'light', false);
    }

    private applyTheme(scheme: ThemeMode, persist = true): void {
        setColorScheme(scheme, this.document.documentElement);
        this.mode.set(scheme);
        if (persist) {
            try {
                localStorage.setItem(THEME_STORAGE_KEY, scheme);
            } catch {
                // Storage unavailable (private mode); the choice just isn't remembered.
            }
        }
    }
}
